/**
 * Budget — /budget (vraag Joris 27 sep 2026).
 *
 * Per account: realisatie van de afgelopen jaren (Gripp-facturen), forecast lopend jaar,
 * target volgend jaar, al getekend, gewogen pijplijn en het gat. Klik op een account voor
 * het detailpaneel; export in de kolomindeling van het Revenue-blad van de reforecast-Excel.
 * Rekenregels: ai-engine/services/budget.py · plan: docs/PLAN-BUDGET-2027.md.
 *
 * 1 okt 2026: nieuwe klant toevoegen (handmatige regel, later te koppelen), retainer en
 * propositie per regel (afgeleid of handmatig), filter op retainer en totalen per retainer-split
 * en per propositie boven de tabel.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddLinkIcon from '@mui/icons-material/AddLink';
import PageHeader from '../../ui-components/PageHeader';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import { useUserRole } from '../../contexts/UserRoleContext';
import { budgetApi } from './budgetApi';
import {
  RETAINER_FILTERS, euro, factuurPlusLopend, filterLabel, filterRetainer, gatTekst, groei, groepeer, koppelingTekst,
  totalenVoorFilter,
} from './budgetLogica';
import BudgetPaneel from './BudgetPaneel';
import GewichtenDialoog from './GewichtenDialoog';
import ImportDialoog from './ImportDialoog';
import TargetCel from './TargetCel';
import OpmerkingCel from './OpmerkingCel';
import KoppelDialoog from './KoppelDialoog';
import NieuweKlantDialoog from './NieuweKlantDialoog';
import NieuwKoppelDialoog, { wijzigKoppeling } from './NieuwKoppelDialoog';
import TypeCel from './TypeCel';

const KOP = { fontSize: 12, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' };
const NUM = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

const LINK = {
  ml: 0.75, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 400,
  color: '#94a3b8', verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center', gap: 0.25,
  '&:hover': { color: '#4f46e5' },
};

function B({ children }) {
  return <Box component="b" sx={{ color: '#334155', fontWeight: 600, ...NUM }}>{children}</Box>;
}

/** Twee rustige regels boven de tabel: retainer-split en target per propositie (uit de engine). */
function Splitsing({ totaal, jaar, vorig }) {
  const split = totaal?.retainer_split;
  const props = (totaal?.proposities || []).filter((p) => p.target || p.realisatie_vorig);
  if (!split) return null;
  const pct = (a) => (a == null ? '' : ` (${Math.round(a)}%)`);
  const deel = (label, d) => (
    <Box component="span" sx={{ mr: 3, display: 'inline-block' }}>
      {label}: <B>{euro(d.target) || '€ 0'}</B>{pct(d.aandeel)} · RF02 {euro(d.rf02) || '€ 0'} · gefactureerd {vorig} {euro(d.realisatie_vorig) || '€ 0'} · lopend {euro(d.lopend) || '€ 0'}
    </Box>
  );
  return (
    <Box sx={{ mb: 2, fontSize: 12.5, color: '#64748b', lineHeight: 1.9 }} data-testid="budget-splitsing">
      <Box>
        <Box component="span" sx={{ mr: 1.5, fontWeight: 700, color: '#475569' }}>Target {jaar}</Box>
        {deel('Retainer', split.retainer)}{deel('Geen retainer', split.geen)}
      </Box>
      {props.length > 0 && (
        <Box title="Het target van elk account is verdeeld naar zijn doel-mix (opdrachtwaarde per doel, laatste 24 maanden); handmatig gekozen telt 100%; zonder historie = Onbekend">
          <Box component="span" sx={{ mr: 1.5, fontWeight: 700, color: '#475569' }}>Per propositie</Box>
          {props.map((p) => (
            <Box component="span" key={p.sleutel} sx={{ mr: 2.5, display: 'inline-block' }}>
              {p.label} <B>{euro(p.target) || '€ 0'}</B>{pct(p.aandeel)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Totaal({ label, waarde }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1f2937', ...NUM }}>{waarde || '—'}</Typography>
    </Box>
  );
}

export default function Budget() {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [zoek, setZoek] = useState('');
  const [open, setOpen] = useState(null);
  const [ingeklapt, setIngeklapt] = useState({ zonder: true, leeg: true });
  const [gewichtenOpen, setGewichtenOpen] = useState(false);
  const [exporteren, setExporteren] = useState(false);
  const [rapportBezig, setRapportBezig] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [koppelRegel, setKoppelRegel] = useState(null);
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [nieuwKoppel, setNieuwKoppel] = useState(null);
  const [retainerFilter, setRetainerFilter] = useState('alle');
  const { permissions } = useUserRole() || {};
  const magWijzigen = !!permissions?.budget?.edit;
  const magImporteren = magWijzigen && !!permissions?.admin_settings?.edit;

  const laad = useCallback(async () => {
    setLaden(true);
    try { setData(await budgetApi.overzicht()); } catch (e) { toast.error(`Budget laden mislukt: ${e.message}`); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  const jaar = data?.jaar;
  const vorig = data?.vorig_jaar;
  const groepen = useMemo(() => (data ? groepeer(filterRetainer(data.regels, retainerFilter), zoek, data.vorig_jaar) : []),
    [data, zoek, retainerFilter]);

  // Alleen een handmatige regel zonder koppeling; bevestigen + ongedaan maken (zet precies de stand terug).
  const verwijderNieuw = async (r) => {
    const ok = await bevestig({
      titel: `${r.naam} verwijderen?`,
      tekst: 'De handmatig toegevoegde klant verdwijnt uit het budget, met wat er is ingevuld (target, RF02, opmerking). Direct daarna kun je dit ongedaan maken.',
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      const uit = await budgetApi.verwijderNieuweKlant(r.sleutel, r.versie, jaar);
      await laad();
      biedOngedaanAan(`${r.naam} verwijderd`, async () => {
        await budgetApi.herstelNieuweKlant(uit.herstel, jaar);
        await laad();
      }, { eigenKnop: true });
    } catch (e) {
      toast.error(`Verwijderen mislukt: ${e.message}`);
      if (e.status === 409 || e.status === 404) await laad();
    }
  };

  const ontkoppelNieuw = async (r) => {
    try { await wijzigKoppeling(r, null, jaar, laad); } catch (e) {
      toast.error(`Ontkoppelen mislukt: ${e.message}`);
      if (e.status === 409) await laad();
    }
  };

  const exporteer = async () => {
    setExporteren(true);
    try {
      const blob = await budgetApi.export(jaar);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Budget_${jaar}_revenue.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setExporteren(false); }
  };

  const maakRapport = async () => {
    setRapportBezig(true);
    try {
      const { blob, naam } = await budgetApi.rapport(jaar);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = naam;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || 'Rapport maken mislukt'); } finally { setRapportBezig(false); }
  };

  // KPI-tegels rekenen over dezelfde regels als de tabel (retainer-filter); de splitsing toont het totaal.
  const tot = totalenVoorFilter(data, retainerFilter);
  const metFilter = (label) => filterLabel(label, retainerFilter);
  const totGroei = tot ? groei(tot.target, tot.rf02 || null) : null;
  // Zolang de Excel-targets niet geïmporteerd zijn is de berekende forecast gelijk aan gefactureerd;
  // daarom tonen we gefactureerd t/m vandaag en de RF02 uit de Excel naast elkaar (Joris 28 sep).
  const standKort = data?.stand ? new Date(`${data.stand}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '';
  const gefactureerdKop = data && data.gerealiseerde_maanden > 0 ? `${vorig} t/m ${standKort}` : `${vorig || ''}`;
  const stand = data ? (data.gerealiseerde_maanden > 0
    ? `Realisatie ${vorig} t/m ${standKort}` : `Nog geen realisatie ${vorig}`) : '';

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="savings" title={jaar ? `Budget ${jaar}` : 'Budget'}
        subtitle="Omzet per account, het target voor volgend jaar en hoe we daar komen"
        rightActions={data && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {magImporteren && (
              <Button size="small" onClick={() => setImportOpen(true)} sx={{ textTransform: 'none' }}>Targets {vorig} uit Excel</Button>
            )}
            <Button size="small" onClick={() => setGewichtenOpen(true)} sx={{ textTransform: 'none' }}>Maandverdeling</Button>
            <Button size="small" variant="outlined" onClick={maakRapport} disabled={rapportBezig}
              startIcon={rapportBezig ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}>{rapportBezig ? 'Rapport maken…' : 'Rapport (PDF)'}</Button>
            <Button size="small" variant="contained" disableElevation onClick={exporteer} disabled={exporteren}
              sx={{ textTransform: 'none', fontWeight: 700 }}>{exporteren ? 'Bezig…' : 'Exporteer naar Excel'}</Button>
          </Box>
        )} />

      {laden && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : data && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
            <Totaal label={metFilter(`Realisatie ${jaar - 3}`)} waarde={euro(tot.realisatie[String(jaar - 3)])} />
            <Totaal label={metFilter(`Realisatie ${jaar - 2}`)} waarde={euro(tot.realisatie[String(jaar - 2)])} />
            <Totaal label={metFilter(gefactureerdKop)} waarde={euro(tot.realisatie_vorig)} />
            <Totaal label={metFilter(`RF02 ${vorig}`)} waarde={tot.rf02 ? euro(tot.rf02) : '—'} />
            <Totaal label={metFilter(`Target ${jaar}${totGroei?.pct != null ? ` · ${totGroei.pct >= 0 ? '+' : ''}${totGroei.pct}% op RF02` : ''}`)} waarde={euro(tot.target)} />
            <Totaal label={metFilter('Gat na getekend en lopend werk')} waarde={euro(tot.gat)} />
          </Box>

          <Splitsing totaal={data.totaal} jaar={jaar} vorig={vorig} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Zoek account of bedrijf" value={zoek} onChange={(e) => setZoek(e.target.value)}
              sx={{ width: 300, maxWidth: '100%' }} />
            <ToggleButtonGroup size="small" exclusive value={retainerFilter}
              onChange={(_, v) => v && setRetainerFilter(v)} aria-label="Filter op retainer">
              {RETAINER_FILTERS.map((f) => (
                <ToggleButton key={f.waarde} value={f.waarde} sx={{ textTransform: 'none', py: 0.25, px: 1.25, fontSize: 12.5 }}>{f.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {magWijzigen && (
              <Button size="small" onClick={() => setNieuwOpen(true)} sx={{ textTransform: 'none' }}>+ Nieuwe klant</Button>
            )}
            <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
              {stand} · bron: Gripp-facturen · lopend werk = opdrachtwaarde van projecten in uitvoering (deels al gefactureerd) · pijplijn = open offertes van de laatste 12 maanden · gat = target − getekend − nog niet gefactureerd lopend werk
              {magWijzigen ? ' · Target: typ en druk Enter' : ''}
            </Typography>
          </Box>

          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={KOP}>Account</TableCell>
                    <TableCell sx={KOP} title="Retainer en propositie — grijs = afgeleid uit de opdracht-mix, donker = handmatig">Retainer · propositie</TableCell>
                    {[`${jaar - 3}`, `${jaar - 2}`, gefactureerdKop, 'Lopend werk', 'Factuur + lopend', 'Pijplijn (open)', `RF02 ${vorig}`, `Target ${jaar}`, `Groei op RF02`, 'Getekend', 'Gat'].map((h) => (
                      <TableCell key={h} align="right" sx={KOP}>{h}</TableCell>
                    ))}
                    <TableCell sx={KOP}>Opmerking</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groepen.map((g) => (
                    <React.Fragment key={g.soort}>
                      <TableRow onClick={() => setIngeklapt((x) => ({ ...x, [g.soort]: !x[g.soort] }))} sx={{ cursor: 'pointer', bgcolor: '#fafbfc' }}>
                        <TableCell colSpan={14} sx={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.04em' }}>
                          {ingeklapt[g.soort] && !zoek ? '▸' : '▾'} {g.label} ({g.regels.length})
                        </TableCell>
                      </TableRow>
                      {(!ingeklapt[g.soort] || zoek) && g.regels.map((r) => {
                        const gat = gatTekst(r.gat);
                        const gr = groei(r.target, r.rf02);
                        const fl = factuurPlusLopend(r.realisatie_vorig, r.lopend_waarde, r.rf02);
                        return (
                          <TableRow key={r.sleutel} hover onClick={() => setOpen(r.sleutel)} sx={{ cursor: 'pointer' }}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {r.naam}
                              {r.aantal_bedrijven > 1 && <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}> · {r.aantal_bedrijven} bedrijven</Box>}
                              {r.soort === 'account' && /^rec[A-Za-z0-9]{14}$/.test(r.sleutel) && (
                                <Box component="a" href={`/accounts/${r.sleutel}/workspace`} target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()} title="Accountpagina openen in een nieuw tabblad"
                                  aria-label={`Accountpagina ${r.naam} openen in een nieuw tabblad`}
                                  sx={{ ml: 0.75, color: '#94a3b8', verticalAlign: 'middle', display: 'inline-flex', '&:hover': { color: '#4f46e5' } }}>
                                  <OpenInNewIcon sx={{ fontSize: 15 }} />
                                </Box>
                              )}
                              {r.soort === 'zonder' && magWijzigen && (
                                <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); setKoppelRegel(r); }}
                                  title="Koppel aan account" aria-label={`${r.naam} koppelen aan een account`}
                                  sx={{ ml: 0.75, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit',
                                    fontSize: 12, fontWeight: 400, color: '#94a3b8', verticalAlign: 'middle',
                                    display: 'inline-flex', alignItems: 'center', gap: 0.25, '&:hover': { color: '#4f46e5' } }}>
                                  <AddLinkIcon sx={{ fontSize: 16 }} />Koppel
                                </Box>
                              )}
                              {r.soort === 'nieuw' && (
                                <>
                                  {!r.gekoppeld_aan && (
                                    <Box component="span" sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 400, color: '#94a3b8' }}>nieuw</Box>
                                  )}
                                  {magWijzigen && !r.gekoppeld_aan && (
                                    <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); setNieuwKoppel(r); }}
                                      title="Koppel aan account of Gripp-bedrijf" aria-label={`${r.naam} koppelen`} sx={LINK}>
                                      <AddLinkIcon sx={{ fontSize: 16 }} />Koppel
                                    </Box>
                                  )}
                                  {magWijzigen && r.gekoppeld_aan && (
                                    <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); ontkoppelNieuw(r); }}
                                      title="Koppeling weghalen" aria-label={`${r.naam} ontkoppelen`} sx={LINK}>Ontkoppel</Box>
                                  )}
                                  {magWijzigen && !r.gekoppeld_aan && (
                                    <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); verwijderNieuw(r); }}
                                      title="Handmatig toegevoegde klant verwijderen" aria-label={`${r.naam} verwijderen`} sx={LINK}>Verwijder</Box>
                                  )}
                                  {koppelingTekst(r) && (
                                    <Box component="span" sx={{ display: 'block', fontSize: 11.5, fontWeight: 400,
                                      color: r.koppeling === 'actief' ? '#94a3b8' : '#b54708' }}>{koppelingTekst(r)}</Box>
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 0.25 }}>
                              <TypeCel regel={r} jaar={jaar} magWijzigen={magWijzigen} proposities={data.keuzes?.proposities}
                                onOpgeslagen={laad} />
                            </TableCell>
                            <TableCell align="right" sx={NUM}>{euro(r.realisatie[String(jaar - 3)])}</TableCell>
                            <TableCell align="right" sx={NUM}>{euro(r.realisatie[String(jaar - 2)])}</TableCell>
                            <TableCell align="right" sx={NUM}>{euro(r.realisatie_vorig)}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: '#64748b' }}>{r.lopend_waarde ? euro(r.lopend_waarde) : ''}</TableCell>
                            <TableCell align="right" sx={NUM} title="Gefactureerd + lopend werk (lopend werk kan deels al gefactureerd zijn); % = deel van RF02">
                              {fl.som ? euro(fl.som) : ''}
                              {fl.pct != null && fl.som > 0 && (
                                <Box component="span" sx={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: fl.pct < 80 ? '#b42318' : fl.pct > 120 ? '#b54708' : '#64748b' }}>
                                  {fl.pct}% van RF02
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: '#64748b' }}>{r.pijplijn_open ? euro(r.pijplijn_open) : ''}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, py: 0.25 }}>
                              <TargetCel regel={r} jaar={jaar} magWijzigen={magWijzigen} onOpgeslagen={laad} veld="rf02" label={`RF02 ${vorig}`} />
                            </TableCell>
                            <TableCell align="right" sx={{ ...NUM, fontWeight: 700, py: 0.25 }}>
                              <TargetCel regel={r} jaar={jaar} magWijzigen={magWijzigen} onOpgeslagen={laad} />
                            </TableCell>
                            <TableCell align="right" sx={{ ...NUM, fontWeight: 700, color: !gr ? '#94a3b8' : gr.bedrag > 0 ? '#067647' : gr.bedrag < 0 ? '#b42318' : '#64748b' }}>
                              {gr ? `${gr.bedrag > 0 ? '+' : ''}${euro(gr.bedrag)}` : ''}
                              {gr?.pct != null && <Box component="span" sx={{ display: 'block', fontSize: 11.5, fontWeight: 600 }}>{gr.pct > 0 ? '+' : ''}{gr.pct}%</Box>}
                            </TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: '#64748b' }}>{euro(r.getekend)}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: gat.open ? '#b42318' : '#64748b' }}>{gat.tekst}</TableCell>
                            <TableCell sx={{ py: 0.25 }}>
                              <OpmerkingCel regel={r} jaar={jaar} magWijzigen={magWijzigen} onOpgeslagen={laad} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  {groepen.length === 0 && (
                    <TableRow><TableCell colSpan={14} sx={{ color: '#94a3b8', py: 4, textAlign: 'center' }}>
                      {zoek ? 'Niets gevonden.' : 'Nog geen omzet of accounts gevonden.'}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {data.conflicten?.length > 0 && (
            <Box component="details" sx={{ mt: 2, color: '#64748b', fontSize: 13 }}>
              <summary style={{ cursor: 'pointer' }}>Koppelingen nakijken: {data.conflicten.length} {data.conflicten.length === 1 ? 'bedrijf staat' : 'bedrijven staan'} bij meer dan één account</summary>
              {data.conflicten.map((c) => (
                <Typography key={c.company_id} sx={{ fontSize: 13, mt: 0.5 }}>
                  {c.bedrijf}: telt bij {c.accounts[0]} (ook gekoppeld aan {c.accounts.slice(1).join(', ')})
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}

      {data && (
        <Box component="details" sx={{ mt: 2, color: '#64748b', fontSize: 13 }}>
          <summary style={{ cursor: 'pointer' }}>Verschillen met de reforecast-Excel</summary>
          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
            De tool rekent, de Excel is alleen voor aanlevering. Bewust anders dan het Revenue-blad:
          </Typography>
          {[
            `Realisatie ${vorig} telt t/m vandaag, inclusief de lopende maand (Excel: vast jan–jun).`,
            `De rest van ${vorig} wordt helemaal verdeeld; in de Excel tellen de gewichten jul–dec op tot 0,84, dus 16% van "wat nog te halen" valt daar weg.`,
            `${jaar} heeft 12 maanden; in de Excel alleen jan–jun, en jan/feb verwijzen voor de meeste rijen naar lege cellen (= 0).`,
            'De statusformule (Upsell/Downsell/…) staat hier op elke regel; in de Excel ontbreekt die vanaf rij 100.',
            'Omzet komt uit de Gripp-facturen; de OHW-regel uit de Excel (niet gefactureerd) staat hier als Excel-groep met alleen een target.',
          ].map((t) => <Typography key={t} sx={{ fontSize: 13, mt: 0.5 }}>· {t}</Typography>)}
        </Box>
      )}

      {open && (
        <BudgetPaneel sleutel={open} jaar={jaar} keuzes={data?.keuzes} magWijzigen={magWijzigen}
          onClose={() => setOpen(null)} onGewijzigd={laad} />
      )}
      {data && magWijzigen && (
        <NieuweKlantDialoog open={nieuwOpen} jaar={jaar} regels={data.regels} portfolios={data.keuzes?.portfolios}
          onClose={() => setNieuwOpen(false)} onAangemaakt={laad} />
      )}
      {data && nieuwKoppel && (
        <NieuwKoppelDialoog regel={nieuwKoppel} regels={data.regels} jaar={jaar}
          onClose={() => setNieuwKoppel(null)} onGekoppeld={laad} />
      )}
      {data && koppelRegel && (
        <KoppelDialoog regel={koppelRegel} regels={data.regels} jaar={jaar}
          onClose={() => setKoppelRegel(null)} onGekoppeld={laad} />
      )}
      {data && magImporteren && (
        <ImportDialoog open={importOpen} jaar={jaar} onClose={() => setImportOpen(false)} onGeimporteerd={laad} />
      )}
      {data && (
        <GewichtenDialoog open={gewichtenOpen} jaar={jaar} gewichten={data.gewichten} bron={data.gewichten_bron}
          versie={data.gewichten_versie} magWijzigen={magWijzigen} onClose={() => setGewichtenOpen(false)} onOpgeslagen={laad} />
      )}
    </Box>
  );
}
